import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Spinner, ErrorBanner } from '../../components/ReportTableKit';
import { CertPrepCards, SnapshotTable } from '../../components/certprep/CertPrepCards';
import { ChartCard, TrendChart, BarsChart, DonutChart, SERIES } from '../../components/certprep/CertPrepCharts';
import { CourseLearnersDrawer } from '../../components/certprep/CourseLearnersDrawer';
import { CampaignCompare } from '../../components/certprep/CampaignCompare';
import { PartnerLeaderboard } from '../../components/certprep/PartnerLeaderboard';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/cert-prep/v2`;
const today = () => new Date().toISOString().slice(0, 10);
const EC = SERIES.slice(0, 2);
const VOUCH = [SERIES[2]];
const COMP = [SERIES[1]];

function Filters({ campaigns, value, onChange, compare, onCompare, campaignB, onCampaignB }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const pickCampaign = name => {
    const c = campaigns.find(x => x.name === name);
    onChange({ campaign: name, date_from: c?.default_from || value.date_from, date_to: today() });
  };
  return (
    <div className="filter-bar" data-testid="certprep-filters" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Campaign</label>
      <select className="filter-select" style={{ minWidth: 320, fontWeight: 600 }} value={value.campaign} onChange={e => pickCampaign(e.target.value)} data-testid="certprep-filter-campaign">
        {campaigns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
      </select>
      {!compare && (
        <>
          <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>From</label>
          <input type="date" className="filter-input" value={value.date_from} max={value.date_to} onChange={e => set('date_from', e.target.value)} data-testid="certprep-filter-from" />
          <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>To</label>
          <input type="date" className="filter-input" value={value.date_to} min={value.date_from} onChange={e => set('date_to', e.target.value)} data-testid="certprep-filter-to" />
        </>
      )}
      {compare && (
        <>
          <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>vs</label>
          <select className="filter-select" style={{ minWidth: 320, fontWeight: 600 }} value={campaignB} onChange={e => onCampaignB(e.target.value)} data-testid="certprep-filter-campaign-b">
            {campaigns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </>
      )}
      <label style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: '#194449', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={compare} onChange={e => onCompare(e.target.checked)} data-testid="certprep-compare-toggle" /> Compare campaigns
      </label>
    </div>
  );
}

function Charts({ d, onCourse }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 16, marginBottom: 20 }}>
      <ChartCard title="Weekly Enrollments vs Completions vs Vouchers" testId="certprep-chart-weekly"><TrendChart data={d.weekly} /></ChartCard>
      <ChartCard title="Monthly Enrollments vs Completions vs Vouchers" testId="certprep-chart-monthly"><TrendChart data={d.monthly} /></ChartCard>
      <ChartCard title="Top 10 Certification Courses: Enrollments vs Completions vs Vouchers (click a bar to see learners)" testId="certprep-chart-top-courses" height={420} span={2}>
        <BarsChart data={d.top_courses} horizontal labelWidth={90} onBarClick={onCourse} />
      </ChartCard>
      <ChartCard title="Activity by Domain Type: Enrollments, Completions, Vouchers" testId="certprep-chart-domain-type"><BarsChart data={d.by_domain_type} /></ChartCard>
      <ChartCard title="Enrollments vs Completions by Solution Area" testId="certprep-chart-solution-area"><BarsChart data={d.by_solution_area} keys={EC} /></ChartCard>
      <ChartCard title="Enrollments vs Completions vs Vouchers by Region" testId="certprep-chart-region"><BarsChart data={d.by_region} horizontal labelWidth={80} /></ChartCard>
      <ChartCard title="Top 10 ESI Partner Domains: Voucher Distribution" testId="certprep-chart-esi-domains"><BarsChart data={d.top_esi_domains} keys={VOUCH} horizontal labelWidth={150} /></ChartCard>
      <ChartCard title="Completions by Language and Region" testId="certprep-chart-language-region" height={380}><BarsChart data={d.completions_by_language_region} keys={COMP} horizontal labelWidth={170} /></ChartCard>
      <ChartCard title="Vouchers Issued by Voucher Type: ARB vs GitHub" testId="certprep-chart-voucher-type" height={380}><DonutChart data={d.voucher_types} /></ChartCard>
    </div>
  );
}

export default function CertPrepDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [course, setCourse] = useState(null);
  const [compare, setCompare] = useState(false);
  const [campaignB, setCampaignB] = useState('');

  const toggleCompare = on => {
    setCompare(on);
    if (on && !campaignB) setCampaignB(campaigns.find(c => c.name !== filters?.campaign)?.name || '');
  };

  useEffect(() => {
    axios.get(`${BASE}/campaigns`, { withCredentials: true })
      .then(r => {
        setCampaigns(r.data);
        if (r.data[0]) setFilters({ campaign: r.data[0].name, date_from: r.data[0].default_from, date_to: today() });
        else setLoading(false);
      })
      .catch(() => { setError('Could not load campaigns.'); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!filters?.date_from || !filters?.date_to) return;
    let live = true;
    setLoading(true); setError(null);
    axios.get(`${BASE}/dashboard`, { params: filters, withCredentials: true })
      .then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) setError(e.response?.data?.detail || 'Failed to load Cert Prep data. Please try again.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [filters]);

  return (
    <div data-testid="certprep-page">
      <div className="page-header">
        <h1 data-testid="certprep-title">Certification Prep</h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>Enrollments, completions and vouchers for the selected campaign's eligible courses</p>
      </div>
      {filters && <Filters campaigns={campaigns} value={filters} onChange={setFilters}
        compare={compare} onCompare={toggleCompare} campaignB={campaignB} onCampaignB={setCampaignB} />}
      {compare && filters && <CampaignCompare campaignA={filters.campaign} campaignB={campaignB} />}
      {!compare && error && <ErrorBanner message={error} testId="certprep-error" />}
      {!compare && loading && <div className="table-container"><Spinner /></div>}
      {!compare && !loading && !error && data && (
        <>
          <CertPrepCards cards={data.cards} />
          <Charts d={data} onCourse={setCourse} />
          <PartnerLeaderboard filters={filters}
            onOpen={p => setCourse({ domain: p.domain, label: p.domain, name: `${p.partner_name} · ${p.domain}` })} />
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <SnapshotTable title="Program Snapshot by Domain Type" dimLabel="Domain Type" rows={data.snapshot.domain_type} testId="certprep-snapshot-domain-type" />
            <SnapshotTable title="Program Snapshot by Region" dimLabel="Region" rows={data.snapshot.region} testId="certprep-snapshot-region" />
          </div>
        </>
      )}
      {course && filters && <CourseLearnersDrawer course={course} filters={filters} onClose={() => setCourse(null)} />}
    </div>
  );
}
